import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import PaidIcon from '@mui/icons-material/Paid';
import GroupIcon from '@mui/icons-material/Group';
import PoolIcon from '@mui/icons-material/Pool';
import EmptyState from '../components/common/EmptyState';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import { useAppSelector } from '../hooks/useAppSelector';
import swimService from '../api/services/swimService';
import { ClassLevel } from '../types/swim';
import SwimEnrollmentWizard from '../components/swim/SwimEnrollmentWizard';

interface SwimProgram {
  id: string;
  name: string;
  description: string | null;
  program_type: string;
  status: string;
  is_active: boolean;
  start_date: string;
  end_date: string;
  price_member: number;
  price_non_member: number | null;
  max_participants: number;
  class_level_id: string | null;
  required_consents: string[] | null;
  recurrence_pattern: { days: number[]; time: string; duration: number } | null;
  target_audience?: 'kids' | 'adult' | 'all' | string;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function to12Hour(hhmm: string): string {
  const [hStr, m] = (hhmm || '').split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m ?? '00'} ${period}`;
}

function formatSchedule(p: SwimProgram['recurrence_pattern']): string {
  if (!p) return 'Schedule TBD';
  const days = (p.days || []).map((d) => DAY_NAMES[d] ?? '?').join(', ');
  return `${days} • ${to12Hour(p.time)} (${p.duration} min)`;
}

function ordinalSuffix(n: number): string {
  const j = n % 10;
  const k = n % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

function formatPrettyDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  const month = dt.toLocaleString('en-US', { month: 'long' });
  return `${month} ${d}${ordinalSuffix(d)}, ${y}`;
}

const SwimClassesPage: React.FC = () => {
  const { user } = useAppSelector((s) => s.auth);
  const navigate = useNavigate();
  const [programs, setPrograms] = useState<SwimProgram[]>([]);
  const [levels, setLevels] = useState<ClassLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollingProgram, setEnrollingProgram] = useState<SwimProgram | null>(null);
  const [audienceFilter, setAudienceFilter] = useState<'kids' | 'adult'>('kids');

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [progRes, lvlRes] = await Promise.all([
        axiosInstance.get<{ data: SwimProgram[] }>('/programs/', { params: { type: 'swim' } }),
        swimService.listClassLevels(),
      ]);
      setPrograms(progRes.data.data ?? []);
      setLevels(lvlRes);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load swim classes');
    } finally {
      setLoading(false);
    }
  }

  const levelById = useMemo(() => new Map(levels.map((l) => [l.id, l])), [levels]);

  // Order classes by skill level ladder (Pre-Novice → Novice → Junior/Elite),
  // then by start_date as a tie-breaker. Programs with no level land at the end.
  const sortedPrograms = useMemo(() => {
    const lvlOrder = (id: string | null): number => {
      if (!id) return Number.MAX_SAFE_INTEGER;
      const lvl = levelById.get(id);
      return lvl ? lvl.display_order : Number.MAX_SAFE_INTEGER;
    };
    return [...programs].sort((a, b) => {
      const diff = lvlOrder(a.class_level_id) - lvlOrder(b.class_level_id);
      if (diff !== 0) return diff;
      return (a.start_date || '').localeCompare(b.start_date || '');
    });
  }, [programs, levelById]);

  // Filter by audience tab. 'all'-audience programs appear under both Kids
  // and Adults so we don't have to invent a third tab for them.
  const visiblePrograms = useMemo(
    () =>
      sortedPrograms.filter((p) => {
        const a = (p.target_audience || 'kids').toLowerCase();
        if (a === 'all') return true;
        return a === audienceFilter;
      }),
    [sortedPrograms, audienceFilter],
  );

  const kidsCount = useMemo(
    () =>
      sortedPrograms.filter((p) => {
        const a = (p.target_audience || 'kids').toLowerCase();
        return a === 'kids' || a === 'all';
      }).length,
    [sortedPrograms],
  );
  const adultCount = useMemo(
    () =>
      sortedPrograms.filter((p) => {
        const a = (p.target_audience || 'kids').toLowerCase();
        return a === 'adult' || a === 'all';
      }).length,
    [sortedPrograms],
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">Swim Classes</Typography>
        <Typography variant="body2" color="text.secondary">
          Browse available swim classes and lessons.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <ToggleButtonGroup
        size="small"
        value={audienceFilter}
        exclusive
        onChange={(_e, v) => v && setAudienceFilter(v)}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="kids">Kids ({kidsCount})</ToggleButton>
        <ToggleButton value="adult">Adults ({adultCount})</ToggleButton>
      </ToggleButtonGroup>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : visiblePrograms.length === 0 ? (
        <EmptyState
          icon={<PoolIcon />}
          title={audienceFilter === 'adult' ? 'No adult swim programs yet' : 'No swim classes yet'}
          body={
            audienceFilter === 'adult'
              ? 'Check back soon — Masters and other adult swim programs are coming.'
              : 'The club is preparing the next session — check back soon.'
          }
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
          }}
        >
          {visiblePrograms.map((p) => {
            const level = p.class_level_id ? levelById.get(p.class_level_id) : null;
            return (
              <Card key={p.id} variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                    <Typography variant="h6" component="h2">{p.name}</Typography>
                    {level && <Chip size="small" color="primary" label={level.name} />}
                  </Stack>
                  {p.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {p.description}
                    </Typography>
                  )}
                  <Stack spacing={0.75}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EventIcon fontSize="small" color="action" />
                      <Typography variant="body2">{formatSchedule(p.recurrence_pattern)}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GroupIcon fontSize="small" color="action" />
                      <Typography variant="body2">Max {p.max_participants} swimmers</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PaidIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        ${Number(p.price_member).toFixed(0)} members
                        {p.price_non_member != null && ` • $${Number(p.price_non_member).toFixed(0)} non-members`}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {formatPrettyDate(p.start_date)} → {formatPrettyDate(p.end_date)}
                    </Typography>
                  </Stack>
                  {p.required_consents && p.required_consents.length > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography variant="caption" color="text.secondary">
                        Required consents: {p.required_consents.map(c => c.replace(/_/g, ' ')).join(', ')}
                      </Typography>
                    </>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setEnrollingProgram(p)}
                    disabled={user?.role !== 'member'}
                  >
                    {user?.role === 'member' ? 'Enroll' : 'Members only'}
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      )}

      {enrollingProgram && (
        <SwimEnrollmentWizard
          program={enrollingProgram}
          onClose={() => setEnrollingProgram(null)}
          onEnrolled={() => {
            setEnrollingProgram(null);
            navigate('/swim/my');
          }}
        />
      )}
    </Container>
  );
};

export default SwimClassesPage;
